import { z } from 'zod';

/**
 * One kind of record's id parts: fields of the record, in the order its id holds them (the
 * object's key order). Each part checks a string, finite number or boolean and returns it
 * unchanged, with `.optional()` or `.default()` for a part that may be absent; a coercion or
 * transform would give two values one id, or an id that never decodes.
 */
export type RecordIdParts = z.ZodObject<z.ZodRawShape>;

/** An id no record of the kind could have. */
export class InvalidRecordIdError extends Error {
  readonly kind: string;
  readonly id: string;

  constructor(kind: string, id: string) {
    super(`Invalid ${kind} id`);
    this.name = 'InvalidRecordIdError';
    this.kind = kind;
    this.id = id;
  }
}

const toBase64Url = (text: string) => {
  let binary = '';
  new TextEncoder().encode(text).forEach(byte => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const fromBase64Url = (id: string) =>
  new TextDecoder('utf-8', { fatal: true }).decode(
    Uint8Array.from(atob(id.replace(/-/g, '+').replace(/_/g, '/')), char => char.charCodeAt(0))
  );

const encodeParts = (kind: string, parts: RecordIdParts, record: Record<string, unknown>) => {
  const values = Object.entries(parts.shape).map(([key, schema]) => {
    const value = record[key] ?? undefined;
    const parsed = z.safeParse(schema, value);
    // A read never throws (ADR 0004), so a stored part that fails its schema keeps its value
    return parsed.success ? parsed.data : value;
  });
  return toBase64Url(JSON.stringify([kind, ...values.map(value => value ?? null)]));
};

const decodeParts = (kind: string, parts: RecordIdParts, id: string): Record<string, unknown> => {
  const keys = Object.keys(parts.shape);
  let decoded: unknown;
  try {
    decoded = JSON.parse(fromBase64Url(id));
  } catch {
    throw new InvalidRecordIdError(kind, id);
  }
  if (!Array.isArray(decoded) || decoded.length !== keys.length + 1 || decoded[0] !== kind) {
    throw new InvalidRecordIdError(kind, id);
  }

  const partValues: unknown[] = decoded.slice(1);
  const values: Record<string, unknown> = {};
  keys.forEach((key, index) => {
    if (partValues[index] !== null) {
      values[key] = partValues[index];
    }
  });
  const parsed = parts.safeParse(values);
  // Another spelling of the same key, such as spaced JSON or base64 padding, is not the record's id
  if (!parsed.success || encodeParts(kind, parts, parsed.data) !== id) {
    throw new InvalidRecordIdError(kind, id);
  }
  return parsed.data;
};

/**
 * Encodes and decodes the ids of the kinds a registry names. An id is the kind and its parts as
 * compact JSON, base64url-encoded without padding, so no two keys share one.
 */
export const createRecordIdCoder = <R extends Record<string, RecordIdParts>>(registry: R) => {
  // A kind a caller can name is always one the registry holds
  const partsOf = (kind: Extract<keyof R, string>) => registry[kind] as RecordIdParts;

  return {
    encodeRecordId: <K extends Extract<keyof R, string>>(
      kind: K,
      record: { [P in keyof z.input<R[K]>]: unknown }
    ): string => encodeParts(kind, partsOf(kind), record as Record<string, unknown>),

    decodeRecordId: <K extends Extract<keyof R, string>>(kind: K, id: string): z.output<R[K]> =>
      decodeParts(kind, partsOf(kind), id) as z.output<R[K]>,
  };
};
